import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

/** Casca visual compartilhada dos cards de recurso do projeto (Overview). */
export function Pill({ children, tone }: { children: ReactNode; tone: "ok" | "gold" }) {
  const cls =
    tone === "ok"
      ? "bg-[rgba(39,161,85,0.1)] text-[#27a155] border-[rgba(39,161,85,0.25)]"
      : "bg-[rgba(255,200,61,0.15)] text-[#b57a00] border-[rgba(255,200,61,0.3)]";
  return (
    <span className={cn(
      "font-mono text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-[0.08em] flex items-center gap-1.5",
      cls,
    )}>
      {tone === "ok" && (
        <span className="w-1.5 h-1.5 rounded-full bg-[#27a155]" style={{ boxShadow: "0 0 6px #27a155" }} />
      )}
      {children}
    </span>
  );
}

interface ScardShellProps {
  overline: string;
  title: string;
  pill?: ReactNode;
  stat: ReactNode;
  /** `hint` expande um rótulo que é acrônimo. Sem ela, `SWC` fica como uma
   *  sigla que a tela nunca explica — o `title` do slot cobre só o **valor**. */
  meta: { k: string; v: string; mono?: boolean; hint?: string }[];
  cta: ReactNode;
  /** Quando presente, o card navega para esta rota — vira `<Link>`, não
   * `<button>`. Não envolva o resultado num `<a>`/`<Link>` externo: com
   * `to`, a própria casca já é o elemento navegável. */
  to?: string;
}

/** Casca visual compartilhada dos cards de recurso do projeto (Overview). */
export function ScardShell({ overline, title, pill, stat, meta, cta, to }: ScardShellProps) {
  const className =
    "relative h-full w-full text-left bg-white border border-[#e8ebf0] rounded-xl p-6 shadow-[0_1px_2px_rgba(8,37,76,0.04)] flex flex-col gap-4 overflow-hidden hover:border-[#08254c]/40 transition-colors group";
  const content = (
    <>
      <div className="absolute left-0 top-6 w-9 h-[3px] bg-[#ffc83d] rounded-r-sm" />
      <div className="flex justify-between items-start gap-3">
        <div className="flex flex-col gap-1.5">
          <div className="font-mono text-[10px] text-[#596b85] tracking-[0.2em] uppercase font-semibold">{overline}</div>
          <h3 className="font-brand font-bold text-[17px] text-[#08254c] m-0 tracking-[-0.01em]">{title}</h3>
        </div>
        {pill}
      </div>
       {/* Contrato do slot `stat`: centraliza a política de contenção para evitar 
        * que textos longos sejam cortados pelo overflow do card pai.
        *
        * - min-w-0 (slot e filho): reseta o `min-width: auto` do flexbox, permitindo encolher.
        * - flex-wrap (filho): força a quebra de linha em telas menores.
        * - break-words (descendentes): parte tokens longos sem espaço interno.
        * - data-slot: seletor estável para testes de integração. */}
      <div className="min-w-0 [&>*]:min-w-0 [&>*]:flex-wrap [&_*]:break-words" data-slot="stat">
        {stat}
      </div>
      {/* Sem linha de detalhe, o bloco sai inteiro: a borda sozinha lê como
          "faltou carregar". */}
      {meta.length > 0 && (
        <div className="grid grid-cols-2 gap-3 pt-3.5 border-t border-[#f2f4f7]">
          {meta.map((m) => (
            <div key={m.k} className="flex flex-col gap-0.5 min-w-0">
              <span
                title={m.hint}
                className={cn(
                  "font-mono text-[10px] text-[#7b8799] tracking-[0.08em] uppercase",
                  m.hint && "decoration-dotted underline underline-offset-2 cursor-help",
                )}
              >
                {m.k}
              </span>
              <span
                title={m.v}
                className={cn(
                  "text-[13px] text-[#08254c] font-semibold tracking-[-0.005em] truncate",
                  m.mono && "font-mono font-medium",
                )}
              >
                {m.v}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between pt-2.5 mt-auto border-t border-[#f2f4f7] text-xs text-[#08254c] font-semibold group-hover:text-[#b57a00]">
        {cta}
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" className={className}>
      {content}
    </button>
  );
}
